import { createContext, useState, useEffect, useContext } from 'react';
import { jobApi } from '../services/api.service';

// Create context
const JobContext = createContext();

// Custom hook to use the job context
export const useJob = () => useContext(JobContext);


export function JobProvider({ children }) {
  // State for jobs management
  const [currentJob, setCurrentJob] = useState(null);
  const [jobList, setJobList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load jobs on component mount
  useEffect(() => {
    loadJobs();
    
    // Load saved job selection from local storage
    const savedJobId = localStorage.getItem('currentJobId');
    if (savedJobId) {
      // We'll populate this after loading the job list
      loadSavedJob(savedJobId);
    }
  }, []);
  
  // Load saved job after job list is populated
  const loadSavedJob = (jobId) => {
    if (jobList.length > 0) {
      const job = jobList.find(j => j.id === jobId);
      if (job) {
        setCurrentJob(job);
      } else if (jobList.length > 0) {
        // If saved job not found, default to first job
        setCurrentJob(jobList[0]);
      }
    }
  };

  // Load all jobs
  const loadJobs = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Use the jobApi service for loading jobs
      // This works both for mock data (persisted in localStorage) and real API
      const response = await jobApi.getAllJobs();
      
      // API response format: { jobs: [...], totalCount: n, offset: n, limit: n }
      const jobs = response.data.jobs || [];
      setJobList(jobs);
      
      // Set current job if none selected
      if (!currentJob && jobs.length > 0) {
        setCurrentJob(jobs[0]);
      }
    } catch (err) {
      console.error('Error loading jobs:', err);
      setError('Failed to load jobs. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Select a job
  const selectJob = (job) => {
    setCurrentJob(job);
    localStorage.setItem('currentJobId', job.id);
  };

  // Create a new job
  const createJob = async (jobData) => {
    setLoading(true);
    setError(null);
    
    try {
      // Handle customJobId for API format
      let apiJobData = { ...jobData };
      
      // If custom ID is provided, use it as id
      if (jobData.customJobId && jobData.customJobId.trim() !== '') {
        apiJobData.id = jobData.customJobId;
      }
      
      // Remove customJobId from API data
      delete apiJobData.customJobId;
      
      console.log('Creating job with data:', JSON.stringify(apiJobData, null, 2));
      
      // Call API to create job (real or mock)
      const response = await jobApi.createJob(apiJobData);
      const newJob = response.data;
      
      // Update job list in context
      const updatedJobList = [...jobList, newJob];
      setJobList(updatedJobList);
      
      // Select the new job
      selectJob(newJob);
      
      return newJob;
    } catch (err) {
      console.error('Error creating job:', err);
      console.error('Error response:', err.response);
      console.error('Error data:', err.response?.data);
      console.error('Error status:', err.response?.status);
      
      // Handle CSRF token errors specifically
      if (err.message?.includes('CSRF') || err === 'CSRF token validation failed. Your session may have expired. Please refresh the page and try again.') {
        setError('Your session has expired. Please refresh the page and try again.');
        // Clear the CSRF token to force a refresh
        localStorage.removeItem('csrfToken');
      } else {
        setError('Failed to create job. Please try again.');
      }
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Update a job
  const updateJob = async (jobId, jobData) => {
    setLoading(true);
    setError(null);
    
    try {
      // Handle customJobId for API format
      let apiJobData = { ...jobData };
      const newJobId = apiJobData.customJobId;
      
      // If custom ID is provided and different from current ID, it's an ID change
      if (newJobId && newJobId.trim() !== '' && newJobId !== jobId) {
        // First, check if the new ID already exists
        const idExists = jobList.some(job => job.id === newJobId && job.id !== jobId);
        if (idExists) {
          throw new Error(`Job ID ${newJobId} already exists`);
        }
        
        // Set the new ID in the API data
        apiJobData.id = newJobId;
      }
      
      // Remove customJobId from API data
      delete apiJobData.customJobId;
      
      // Call API to update job (real or mock)
      const response = await jobApi.updateJob(jobId, apiJobData);
      const updatedJob = response.data;
      
      // If the ID changed, we need to update differently
      if (newJobId && newJobId !== jobId) {
        // Remove the old job
        const filteredJobList = jobList.filter(job => job.id !== jobId);
        
        // Add the updated job with the new ID
        const updatedJobList = [...filteredJobList, updatedJob];
        setJobList(updatedJobList);
        
        // Update localStorage with new job ID if current job is being updated
        if (currentJob && currentJob.id === jobId) {
          localStorage.setItem('currentJobId', updatedJob.id);
        }
      } else {
        // Standard update without changing ID
        setJobList(jobList.map(job => job.id === updatedJob.id ? updatedJob : job));
      }
      
      // Update current job if it's the one being edited
      if (currentJob && currentJob.id === jobId) {
        setCurrentJob(updatedJob);
      }
      
      return updatedJob;
    } catch (err) {
      console.error('Error updating job:', err);
      setError('Failed to update job. Please try again.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Delete a job
  const deleteJob = async (jobId) => {
    setLoading(true);
    setError(null);
    
    try {
      // Call API to delete job (real or mock)
      await jobApi.deleteJob(jobId);
      
      // Remove the job from the list
      const updatedJobList = jobList.filter(job => job.id !== jobId);
      setJobList(updatedJobList);
      
      // If current job is deleted, select the first available job
      if (currentJob && currentJob.id === jobId) {
        if (updatedJobList.length > 0) {
          selectJob(updatedJobList[0]);
        } else {
          setCurrentJob(null);
          localStorage.removeItem('currentJobId');
        }
      }
      
      return true;
    } catch (err) {
      console.error('Error deleting job:', err);
      setError('Failed to delete job. Please try again.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Clear any error messages
  const clearError = () => {
    setError(null);
  };

  // Update job equipment
  const updateJobEquipment = async (jobId, siteId, equipmentData) => {
    setLoading(true);
    setError(null);
    
    try {
      // Call API to update equipment
      const response = await jobApi.updateJobEquipment(jobId, siteId, equipmentData);
      const savedEquipment = response.data;
      
      // Find the job in the list
      const jobIndex = jobList.findIndex(job => job.id === jobId);
      if (jobIndex === -1) {
        throw new Error(`Job not found: ${jobId}`);
      }
      
      // Create a deep copy of the job
      const updatedJob = JSON.parse(JSON.stringify(jobList[jobIndex]));
      
      // Find the site in the job
      const siteIndex = updatedJob.sites.findIndex(site => site.id === siteId);
      if (siteIndex === -1) {
        throw new Error(`Site not found: ${siteId}`);
      }
      
      // Initialize equipment array if it doesn't exist
      if (!updatedJob.sites[siteIndex].equipment) {
        updatedJob.sites[siteIndex].equipment = [];
      }
      
      // Check if equipment already exists
      const equipmentId = savedEquipment.id;
      const existingEquipmentIndex = updatedJob.sites[siteIndex].equipment.findIndex(item => 
        (typeof item === 'object' && item.id === equipmentId) || 
        (typeof item === 'string' && item === equipmentData.id)
      );
      
      // Update or add equipment in the site
      if (existingEquipmentIndex !== -1) {
        updatedJob.sites[siteIndex].equipment[existingEquipmentIndex] = savedEquipment;
      } else {
        updatedJob.sites[siteIndex].equipment.push(savedEquipment);
      }
      
      // Update job list
      const updatedJobList = [...jobList];
      updatedJobList[jobIndex] = updatedJob;
      setJobList(updatedJobList);
      
      // Update current job if it's the one being edited
      if (currentJob && currentJob.id === jobId) {
        setCurrentJob(updatedJob);
      }
      
      return savedEquipment;
    } catch (err) {
      console.error('Error updating job equipment:', err);
      setError('Failed to update equipment. Please try again.');
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  // Delete job equipment
  const deleteJobEquipment = async (jobId, siteId, equipmentId) => {
    setLoading(true);
    setError(null);
    
    try {
      // Call API to delete equipment
      await jobApi.deleteJobEquipment(jobId, siteId, equipmentId);
      
      // Find the job in the list
      const jobIndex = jobList.findIndex(job => job.id === jobId);
      if (jobIndex === -1) {
        throw new Error(`Job not found: ${jobId}`);
      }
      
      // Create a deep copy of the job
      const updatedJob = JSON.parse(JSON.stringify(jobList[jobIndex]));
      
      // Find the site in the job
      const siteIndex = updatedJob.sites.findIndex(site => site.id === siteId);
      if (siteIndex === -1) {
        throw new Error(`Site not found: ${siteId}`);
      }
      
      // Remove equipment from the site
      if (updatedJob.sites[siteIndex].equipment) {
        updatedJob.sites[siteIndex].equipment = updatedJob.sites[siteIndex].equipment.filter(item => 
          (typeof item === 'object' && item.id !== equipmentId) ||
          (typeof item === 'string' && item !== equipmentId)
        );
      }
      
      // Update job list
      const updatedJobList = [...jobList];
      updatedJobList[jobIndex] = updatedJob;
      setJobList(updatedJobList);
      
      // Update current job if it's the one being edited
      if (currentJob && currentJob.id === jobId) {
        setCurrentJob(updatedJob);
      }
      
      return true;
    } catch (err) {
      console.error('Error deleting job equipment:', err);
      setError('Failed to delete equipment. Please try again.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Load specific job details
  const loadJobDetails = async (jobId) => {
    setLoading(true);
    setError(null);
    
    try {
      // First try to find in the jobList
      const existingJob = jobList.find(job => job.id === jobId);
      if (existingJob) {
        setCurrentJob(existingJob);
        return existingJob;
      }
      
      // If not found in the list, make an API call
      const response = await jobApi.getJobById(jobId);
      const job = response.data;
      
      if (job) {
        setCurrentJob(job);
        
        // Add to job list if not already there
        if (!jobList.some(j => j.id === job.id)) {
          setJobList(prevJobs => [...prevJobs, job]);
        }
        
        return job;
      } else {
        throw new Error(`Job not found: ${jobId}`);
      }
    } catch (err) {
      console.error(`Error loading job details for ${jobId}:`, err);
      setError('Failed to load job details. Please try again.');
      
      
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Context value
  const value = {
    currentJob,
    jobList,
    loading,
    error,
    selectJob,
    loadJobs,
    loadJobDetails,
    createJob,
    updateJob,
    deleteJob,
    updateJobEquipment,
    deleteJobEquipment,
    clearError
  };

  return <JobContext.Provider value={value}>{children}</JobContext.Provider>;
}

export default JobProvider;